import { CSSProperties } from 'react';
import { ColumnDescription } from 'react-bootstrap-table-next';
import { useSelector } from 'react-redux';
import { getSessionActiveShop, getSessionUser } from '../../../global/session/selectors';
import { SortOrderDirection } from '../../../global/types';
import sortCaretFunc from '../../../global/utils/sortCaretFunc';
import { loaderFormatter } from '../../../helpers/formatters';
import { ShopEntity, UserRole } from '../../../schemas';
import {
  actionsFormatter,
  categoriesFormatter,
  imageFormatter,
  inventoryFormatter,
  nameFormatter,
  priceFormatter,
  statusFormatter,
} from '../components/WarehouseTable/formatters';
import { NonSortableWarehouseColumn, SortableWarehouseColumn } from '../types';
import { mapSortOrder } from '../utils';
import useOnSortChange from './useOnSortChange';

const useWarehouseTableColumns = (): {
  columns: ColumnDescription[];
  loadingColumns: ColumnDescription[];
} => {
  const onSortChange = useOnSortChange();

  const shop: ShopEntity | null = useSelector(getSessionActiveShop);
  const user = useSelector(getSessionUser);

  const sortFunc = (columnName: SortableWarehouseColumn, sortDirection: SortOrderDirection) => {
    onSortChange(mapSortOrder(columnName, sortDirection));
  };

  const columns = [
    {
      dataField: NonSortableWarehouseColumn.image,
      formatter: imageFormatter,
      headerStyle: () => ({ paddingLeft: 0, width: '50px' } as CSSProperties),
      style: () => ({ paddingLeft: 0, userSelect: 'none' } as CSSProperties),
      text: '',
    },
    {
      dataField: SortableWarehouseColumn.name,
      formatter: nameFormatter,
      headerStyle: () => ({ width: '40%' }),
      onSort: sortFunc,
      sort: true,
      sortCaret: sortCaretFunc,
      text: 'Name',
    },
    {
      dataField: SortableWarehouseColumn.vendor,
      hidden: !(user && (user.role === UserRole.Admin || user.role === UserRole.Brand)),
      formatter: (value: string) => value,
      onSort: sortFunc,
      sort: true,
      sortCaret: sortCaretFunc,
      text: 'Vendor',
    },
    {
      dataField: SortableWarehouseColumn.inventory,
      formatter: inventoryFormatter,
      onSort: sortFunc,
      sort: true,
      sortCaret: sortCaretFunc,
      text: 'Inventory',
    },
    {
      dataField: SortableWarehouseColumn.price,
      formatExtraData: { shop },
      formatter: priceFormatter,
      onSort: sortFunc,
      sort: true,
      sortCaret: sortCaretFunc,
      text: 'Price',
    },
    {
      dataField: SortableWarehouseColumn.status,
      formatter: statusFormatter,
      onSort: sortFunc,
      sort: true,
      sortCaret: sortCaretFunc,
      text: 'Status',
    },
    {
      dataField: NonSortableWarehouseColumn.categories,
      formatter: categoriesFormatter,
      text: 'Categories',
    },
    {
      dataField: '',
      text: '',
      formatter: actionsFormatter,
      headerStyle: () => ({ width: '4%' }),
    },
  ];

  const loadingColumns = columns.map((column) => ({
    ...column,
    formatter: loaderFormatter,
  }));

  return { columns, loadingColumns };
};

export default useWarehouseTableColumns;
